import { createContext, useContext, useEffect } from "react";
import type { MusicContextValue } from "../../types/contexts";

/**
 * MusicContext：单独拆出以避免 `MusicContext/index.tsx ↔ PopupPlayer/index.tsx`
 * 的循环依赖（Provider 内部需要渲染 PopupPlayer，而 PopupPlayer 需要通过
 * useMusicContext 读取 Context；两者共用同一个 createContext 对象即可打破环）。
 */
export const MusicContextRef = createContext<MusicContextValue | null>(null);

function useMusicContextRaw(): MusicContextValue {
  const context = useContext(MusicContextRef);
  if (!context) {
    throw new Error(
      "useMusicContext must be used within a MusicContextProvider"
    );
  }
  return context;
}

/**
 * 业务使用的 MusicContext 消费 Hook：读取 context，并在首次挂载时触发 initMusic。
 * 这与 MusicContext/index.tsx 里原来的 useMusicContext 一致。
 */
export function useMusicContext() {
  const context = useMusicContextRaw();

  const {
    setPlayerShow,
    playlist,
    playing,
    setPlaying,
    play,
    pause,
    volume,
    setVolume,
    currentMusic,
    musicList,
    playMode,
    changePlayMode,
    prev,
    next,
    seek,
    currentMusicInstance,
    playMusic,
    cacheMusic,
    initMusic,
    inited,
  } = context;

  useEffect(() => {
    if (inited) return;
    initMusic();
  }, [inited, initMusic]);

  return {
    setPlayerShow,
    playlist,
    playing,
    setPlaying,
    play,
    pause,
    volume,
    setVolume,
    currentMusic,
    musicList,
    playMode,
    changePlayMode,
    prev,
    next,
    seek,
    currentMusicInstance,
    playMusic,
    cacheMusic,
  };
}
